import React, { useState, useEffect } from 'react'
import { Dropdown, Badge, Button, Modal } from 'react-bootstrap'
import { LuBell } from 'react-icons/lu'
import { FaUserCircle, FaSearch, FaPhoneAlt, FaEnvelope, FaQuestionCircle, FaTrash } from 'react-icons/fa'
import { useAuth } from '../AuthContext'
import Cookies from 'js-cookie'
import { clearAuthCookies } from '../../utils/instituteIdCookie'
import { useInstitute } from './InstituteContext'
import axios from 'axios'
import toast from 'react-hot-toast'
import { BASE_URL } from './ApiEndpoints'
import {useNavigate} from 'react-router-dom'

const ProfileDropdown = ({ 
  userName: propUserName, 
  userType: propUserType,
  logout: propLogout,
  notificationCount: propNotificationCount = 3,
  showNotifications = true,
  ...props 
}) => {
  const { logout: authLogout } = useAuth()
  const { logo } = useInstitute()
  const [userName, setUserName] = useState(propUserName || 'Admin')
  const [userType, setUserType] = useState(propUserType || 'Admin')
  const [notificationCount, setNotificationCount] = useState(propNotificationCount)
  const [showSupportDetails, setShowSupportDetails] = useState(false)


  const navigate = useNavigate()
  // Delete account modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleting, setDeleting] = useState(false)

  // Get user information from cookie if not provided as props
  useEffect(() => {
    if (!propUserName || !propUserType) {
      const userCookie = Cookies.get('user')
      if (userCookie) {
        try {
          const user = JSON.parse(userCookie)
          if (user.user_type) {
            const formattedUserType = user.user_type.charAt(0).toUpperCase() + user.user_type.slice(1)
            setUserType(formattedUserType)
            setUserName(formattedUserType)
          }
        } catch (err) {
          console.error('Failed to parse user cookie', err)
        }
      }
    } else {
      setUserName(propUserName)
      setUserType(propUserType)
    }
  }, [propUserName, propUserType])

  // Update notification count when prop changes
  useEffect(() => {
    setNotificationCount(propNotificationCount)
  }, [propNotificationCount])

  const handleLogout = () => {
    clearAuthCookies()
    if (propLogout) {
      propLogout()
    } else {
      authLogout()
    }
  }

  // const handleToggleSupport = () => setShowSupportDetails((prev) => !prev)

  const handleSupportClick = () => {
  const userCookie = Cookies.get('user')
  if (userCookie) {
    try {
      const user = JSON.parse(userCookie)
      if (user.user_type === 'parents') {
        navigate('/parent/raise-query')
        return
      }
    } catch (err) {
      console.error('Failed to parse user cookie', err)
    }
  }
  // For non-parent users, keep the existing toggle behavior
  setShowSupportDetails((prev) => !prev)
}


  // ─── Delete account ───────────────────────────────────────────────────────
  const handleDeleteAccount = async () => {
    setDeleting(true)
    try {
      const userCookie = Cookies.get('user')
      let token = null
      if (userCookie) {
        const user = JSON.parse(userCookie)
        token = user.token
      }

      await axios.delete(`${BASE_URL}/account`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      })

      toast.success('Your account has been deleted successfully.')
      setShowDeleteModal(false)

      // Small delay so user can read the toast, then logout
      setTimeout(() => {
        if (propLogout) {
          propLogout()
        } else {
          authLogout()
        }
      }, 1500)
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete account. Please try again.')
    } finally {
      setDeleting(false)
    }
  }
  // ─────────────────────────────────────────────────────────────────────────

  const defaultPopperConfig = {
    strategy: 'fixed',
    modifiers: [
      { name: 'offset', options: { offset: [0, 8] } },
      {
        name: 'preventOverflow',
        options: { boundary: 'viewport', padding: 8 },
      },
    ],
  }

  const { popperConfig, drop = 'down', ...dropdownProps } = props

  return (
    <>
      <style>
        {`
          .custom-badge {
            background-color: #223843 !important;
          }

          /* Delete modal overlay */
          .delete-modal-backdrop {
            position: fixed;
            inset: 0;
            background: rgba(0, 0, 0, 0.50);
            z-index: 1055;
            display: flex;
            align-items: center;
            justify-content: center;
          }

          .delete-modal-box {
            background: #ffffff;
            border-radius: 14px;
            width: 400px;
            max-width: 92vw;
            border: 1px solid #E5E7EB;
            box-shadow: 0 20px 48px rgba(0, 0, 0, 0.18);
            overflow: hidden;
          }

          .delete-btn-cancel {
            background: #ffffff;
            border: 1px solid #D1D3D4;
            color: #223843;
            border-radius: 8px;
            padding: 9px 20px;
            font-size: 14px;
            font-weight: 500;
            cursor: pointer;
            transition: background 0.15s;
            font-family: Inter, system-ui, sans-serif;
          }
          .delete-btn-cancel:hover { background: #F3F4F6; }
          .delete-btn-cancel:disabled { opacity: 0.6; cursor: not-allowed; }

          .delete-btn-confirm {
            background: #E24B4A;
            border: none;
            color: #ffffff;
            border-radius: 8px;
            padding: 9px 20px;
            font-size: 14px;
            font-weight: 600;
            cursor: pointer;
            transition: background 0.15s;
            font-family: Inter, system-ui, sans-serif;
          }
          .delete-btn-confirm:hover:not(:disabled) { background: #A32D2D; }
          .delete-btn-confirm:disabled { opacity: 0.65; cursor: not-allowed; }

          .dropdown-item-delete {
            display: flex;
            align-items: center;
            gap: 8px;
            width: 100%;
            padding: 6px 16px;
            background: transparent;
            border: none;
            text-align: left;
            cursor: pointer;
            font-size: 14px;
            color: #E24B4A;
            font-weight: 500;
            transition: background 0.15s;
          }
          .dropdown-item-delete:hover { background: #FCEBEB; }
        `}
      </style>

      <div className="d-flex align-items-center gap-2" style={{ flexShrink: 0 }}>
        {/* Profile Dropdown */}
        <Dropdown
          autoClose="outside"
          drop={drop}
          popperConfig={popperConfig ?? defaultPopperConfig}
          {...dropdownProps}
        >
          <Dropdown.Toggle
            variant="link"
            id="profile-dropdown"
            style={{
              textDecoration: 'none',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '8px 12px',
              borderRadius: '10px',
              border: 'none',
              color: '#223843',
              backgroundColor: 'transparent',
              transition: 'all 0.3s ease',
            }}
            className="p-0"
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent'
              e.currentTarget.style.transform = 'scale(1.05)'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent'
              e.currentTarget.style.transform = 'scale(1)'
            }}
          >
            <FaUserCircle
              style={{
                fontSize: '28px',
                color: '#223843',
                cursor: 'pointer',
                filter: 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.2))',
              }}
            />
          </Dropdown.Toggle>

          <Dropdown.Menu
            align="end"
            renderOnMount
            style={{
              marginTop: '10px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              zIndex: 9999,
            }}
          >
            {/* User name / type */}
            <Dropdown.ItemText>
              <div style={{ fontWeight: '600', fontSize: '14px', color: '#223843' }}>
                {userName}
              </div>
              <div style={{ fontSize: '12px', color: '#BF4646' }}>{userType}</div>
            </Dropdown.ItemText>

            {/* Support */}
            {/* <Dropdown.Item onClick={handleToggleSupport} style={{ cursor: 'pointer' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <FaQuestionCircle style={{ fontSize: '14px', color: '#BF4646' }} />
                <span style={{ color: '#223843' }}>
                  {showSupportDetails ? 'Hide Support' : 'Support'}
                </span>
              </div>
            </Dropdown.Item> */}

            <Dropdown.Item onClick={handleSupportClick} style={{ cursor: 'pointer' }}>
  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
    <FaQuestionCircle style={{ fontSize: '14px', color: '#BF4646' }} />
    <span style={{ color: '#223843' }}>
      {showSupportDetails ? 'Hide Support' : 'Support'}
    </span>
  </div>
</Dropdown.Item>

            {showSupportDetails && (
              <Dropdown.ItemText style={{ padding: '8px 12px 12px 12px' }}>
                <div
                  style={{
                    border: '1px solid #E5E7EB',
                    borderRadius: '10px',
                    background: '#F9FAFB',
                    padding: '12px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '10px',
                  }}
                >
                  <div style={{ fontSize: '13px', color: '#223843', fontWeight: 600, lineHeight: '1.4' }}>
                    Reach out for support &amp; services
                  </div>
                  <a
                    href="tel:+919354387864"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      textDecoration: 'none',
                      color: '#223843',
                      fontSize: '13px',
                    }}
                  >
                    <FaPhoneAlt style={{ color: '#BF4646', fontSize: '12px' }} />
                    <span>+91 9354387864</span>
                  </a>
                  <a
                    href="mailto:vibhumedusys@gmail.com"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      textDecoration: 'none',
                      color: '#223843',
                      fontSize: '13px',
                    }}
                  >
                    <FaEnvelope style={{ color: '#BF4646', fontSize: '12px' }} />
                    <span>vibhumedusys@gmail.com</span>
                  </a>
                </div>
              </Dropdown.ItemText>
            )}

            {/* ── Delete Account ── */}
            <button
              className="dropdown-item-delete"
              onClick={() => setShowDeleteModal(true)}
            >
              <FaTrash style={{ fontSize: '13px' }} />
              Delete account
            </button>

            <Dropdown.Divider />

            {/* Logout */}
            <Dropdown.Item onClick={handleLogout} style={{ cursor: 'pointer' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <FaUserCircle style={{ fontSize: '16px' }} />
                <span style={{ color: '#223843' }}>Logout</span>
              </div>
            </Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>
      </div>

      {/* ── Delete Account Confirmation Modal ── */}
      {showDeleteModal && (
        <div
          className="delete-modal-backdrop"
          onClick={() => { if (!deleting) setShowDeleteModal(false) }}
        >
          <div
            className="delete-modal-box"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="delete-modal-title"
          >
            {/* Header */}
            <div style={{ padding: '28px 28px 0' }}>
              {/* Icon */}
              <div
                style={{
                  width: 52,
                  height: 52,
                  borderRadius: '50%',
                  background: '#FCEBEB',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: 18,
                }}
              >
                <FaTrash style={{ fontSize: 20, color: '#E24B4A' }} />
              </div>

              <h5
                id="delete-modal-title"
                style={{
                  fontWeight: 700,
                  fontSize: 18,
                  color: '#111827',
                  margin: '0 0 10px',
                  fontFamily: 'Inter, system-ui, sans-serif',
                }}
              >
                Delete your account?
              </h5>

              <p
                style={{
                  fontSize: 14,
                  color: '#6B7280',
                  lineHeight: 1.65,
                  margin: '0 0 14px',
                  fontFamily: 'Inter, system-ui, sans-serif',
                }}
              >
                This will permanently deactivate your account. You will be logged
                out immediately and will no longer be able to sign in.
              </p>

              {/* Warning banner */}
              <div
                style={{
                  background: '#FCEBEB',
                  borderRadius: 8,
                  padding: '10px 14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  marginBottom: 4,
                }}
              >
                <span style={{ fontSize: 15, color: '#E24B4A' }}>⚠</span>
                <span
                  style={{
                    fontSize: 13,
                    color: '#A32D2D',
                    fontWeight: 500,
                    fontFamily: 'Inter, system-ui, sans-serif',
                  }}
                >
                  This action cannot be undone.
                </span>
              </div>
            </div>

            {/* Footer buttons */}
            <div
              style={{
                padding: '22px 28px 28px',
                display: 'flex',
                gap: 10,
                justifyContent: 'flex-end',
              }}
            >
              <button
                className="delete-btn-cancel"
                onClick={() => setShowDeleteModal(false)}
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                className="delete-btn-confirm"
                onClick={handleDeleteAccount}
                disabled={deleting}
              >
                {deleting ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                      style={{ width: 13, height: 13, borderWidth: 2 }}
                    />
                    Deleting...
                  </>
                ) : (
                  'Yes, delete my account'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default ProfileDropdown
